<template>
  <div class="team-member-project-panels">
    <div class="team-project-col-head caption grey--text text--darken-1 mb-1">
      <span>Project</span>
      <span class="team-project-hours-cell">Used (month)</span>
      <span class="team-project-hours-cell">Min (HH:MM)</span>
      <span class="team-project-hours-cell">Max (HH:MM)</span>
    </div>
    <div
      v-for="row in projects"
      :key="`${memberKey}-${row.project}`"
      class="team-project-row"
    >
      <div class="team-project-col-head team-project-row-head py-2">
        <button
          type="button"
          class="team-project-toggle font-weight-medium text-truncate"
          :aria-expanded="isProjectExpanded(row.project) ? 'true' : 'false'"
          @click="$emit('toggle-project', row.project)"
        >
          <v-icon x-small class="mr-1">
            {{
              isProjectExpanded(row.project)
                ? "mdi-chevron-down"
                : "mdi-chevron-right"
            }}
          </v-icon>
          {{ row.project }}
        </button>
        <span class="team-project-hours-cell">{{ row.monthLoggedLabel }}</span>
        <span
          class="team-project-hours-cell team-budget-editable"
          title="Double-click to edit min"
          @dblclick="
            $emit('edit-budget', memberKey, row.project, 'low', row.low)
          "
        >
          <v-text-field
            v-if="isEditing(memberKey, row.project, 'low')"
            dense
            hide-details
            outlined
            autofocus
            :disabled="saving"
            :value="editValue"
            @input="$emit('update:editValue', $event)"
            @blur="$emit('commit-edit')"
            @keyup.enter="$emit('commit-edit')"
            @keyup.esc="$emit('cancel-edit')"
            @click.stop
          />
          <span v-else>{{ row.low || "—" }}</span>
        </span>
        <span
          class="team-project-hours-cell team-budget-editable"
          title="Double-click to edit max"
          @dblclick="
            $emit('edit-budget', memberKey, row.project, 'high', row.high)
          "
        >
          <v-text-field
            v-if="isEditing(memberKey, row.project, 'high')"
            dense
            hide-details
            outlined
            autofocus
            :disabled="saving"
            :value="editValue"
            @input="$emit('update:editValue', $event)"
            @blur="$emit('commit-edit')"
            @keyup.enter="$emit('commit-edit')"
            @keyup.esc="$emit('cancel-edit')"
            @click.stop
          />
          <span v-else>{{ row.high || "—" }}</span>
        </span>
      </div>
      <div
        v-show="isProjectExpanded(row.project)"
        class="team-project-row-body pt-2 pb-3 pl-6"
      >
        <p v-if="row.entryCount" class="caption grey--text text--darken-1 mb-2">
          {{ row.entryCount }} timesheet line(s). Double-click
          <strong>Hours</strong> to edit; use <strong>Reassign to</strong> to
          move a line. Edits show who changed them.
        </p>
        <KonekoATimesheetEntriesTable
          v-if="row.entries && row.entries.length"
          :entries="row.entries"
          :project-options="projectOptions"
          :reassign-saving-id="reassignSavingId"
          :hours-saving-id="hoursSavingId"
          :can-reassign-entry="canReassignEntry"
          :can-edit-hours="canEditHours"
          :is-editing-hours="isEditingHours"
          :hours-edit-value="hoursEditValue"
          :show-reassign-ui="showReassignUi"
          @reassign="
            (entry, pid, pname) =>
              $emit('reassign', entry, pid, pname, row.project)
          "
          @edit-hours="$emit('edit-hours', $event)"
          @commit-hours-edit="$emit('commit-hours-edit', $event)"
          @cancel-hours-edit="$emit('cancel-hours-edit')"
          @update:hoursEditValue="$emit('update:hoursEditValue', $event)"
        />
        <p
          v-else-if="row.hasMonthTime"
          class="caption amber--text text--darken-3 mb-0"
        >
          Hours are logged but line detail did not load. Click
          <strong>Refresh data</strong> at the top of Koneko A, then expand this
          project again.
        </p>
        <p v-else class="caption grey--text text--darken-1 mb-0">
          No timesheet lines this month on this project.
        </p>
      </div>
    </div>
    <div
      v-if="projects.length"
      class="team-project-col-head team-project-totals-row font-weight-medium grey lighten-4 mt-2 py-2"
    >
      <span class="team-total-label">
        <v-icon x-small class="team-total-label-spacer" aria-hidden="true">
          mdi-chevron-right
        </v-icon>
        Total
      </span>
      <span class="team-project-hours-cell">{{ usedTotalLabel }}</span>
      <span class="team-project-hours-cell">{{ minTotalLabel }}</span>
      <span class="team-project-hours-cell">{{ maxTotalLabel }}</span>
    </div>
  </div>
</template>

<script>
import KonekoATimesheetEntriesTable from "@/components/Koneko/KonekoATimesheetEntriesTable.vue";

export default {
  name: "KonekoATeamMemberProjectPanels",
  components: {KonekoATimesheetEntriesTable},
  props: {
    memberKey: {type: String, required: true},
    projects: {type: Array, default: () => []},
    projectOptions: {type: Array, default: () => []},
    reassignSavingId: {type: [Number, String], default: null},
    hoursSavingId: {type: [Number, String], default: null},
    canReassignEntry: {type: Function, required: true},
    canEditHours: {type: Function, required: true},
    isEditingHours: {type: Function, required: true},
    hoursEditValue: {type: String, default: ""},
    showReassignUi: {type: Boolean, default: false},
    saving: {type: Boolean, default: false},
    editValue: {type: String, default: ""},
    isEditing: {type: Function, required: true},
    isProjectExpanded: {type: Function, required: true},
    minTotalLabel: {type: String, default: "—"},
    maxTotalLabel: {type: String, default: "—"},
    usedTotalLabel: {type: String, default: "—"},
  },
};
</script>

<style scoped>
.team-project-col-head {
  display: grid;
  grid-template-columns: minmax(160px, 1fr) 5.5rem 5.5rem 5.5rem;
  gap: 8px 12px;
  align-items: center;
}

.team-project-hours-cell {
  text-align: right;
  justify-self: end;
  font-variant-numeric: tabular-nums;
}

.team-total-label {
  display: inline-flex;
  align-items: center;
  min-width: 0;
}

.team-total-label-spacer {
  visibility: hidden;
  margin-right: 4px;
}

.team-project-row {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.team-project-row:last-of-type {
  border-bottom: none;
}

.team-project-toggle {
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: #1976d2;
  text-align: left;
  cursor: pointer;
}

.team-project-toggle:hover {
  text-decoration: underline;
}

.team-budget-editable {
  cursor: pointer;
  min-width: 4rem;
}

.team-budget-editable:hover {
  background: rgba(25, 118, 210, 0.06);
  border-radius: 2px;
}

.team-member-project-panels {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding-top: 8px;
}

.team-project-totals-row {
  border-radius: 4px;
}
</style>
